import productForm from "../product-form/product-form";
import productAccordion from "../product-accordion/product-accordion";
import imageModule from "../image/image";
import modalModule from "../modal/modal";
import initTippy from "../../js/lib/tippy";
import { bindSellingPlanToggles } from "../../js/sections/product";

const SECTION_ID = "product-main-ajax";
const BOUND_FLAG = "linkedProductSwapBound";

let swapAbort: AbortController | null = null;
let swapInFlight = false;
let popstateBound = false;

function moveKlaviyoStars(root: Document | Element = document) {
  const productFormReviews = root.querySelector<HTMLElement>(
    ".product-form__reviews"
  );

  if (!productFormReviews) {
    return;
  }

  const productId = productFormReviews.dataset.productId;
  const klaviyoStarRatingWidget = Array.from(
    document.querySelectorAll<HTMLElement>(".klaviyo-star-rating-widget")
  ).find(
    (widget) =>
      !widget.closest(".product-card") &&
      (!productId || !widget.dataset.id || widget.dataset.id === productId)
  );

  if (!klaviyoStarRatingWidget) {
    return;
  }

  klaviyoStarRatingWidget.remove();
  productFormReviews.appendChild(klaviyoStarRatingWidget);
}

function destroySwiper(el: Element | null) {
  const swiperEl = el as HTMLElement & { swiper?: { destroy: Function } };
  if (swiperEl?.swiper) {
    try {
      swiperEl.swiper.destroy(true, true);
    } catch (_e) {
      // ignore destroy errors from partially initialized instances
    }
  }
}

export function initProductGallery(root: Document | Element = document) {
  const SwiperCtor = (window as any).Swiper;
  if (typeof SwiperCtor === "undefined") {
    window.setTimeout(() => initProductGallery(root), 50);
    return;
  }

  const mainEl = root.querySelector(".product-main__swiper") as HTMLElement | null;
  if (!mainEl) {
    return;
  }

  destroySwiper(mainEl);
  const thumbsEl = root.querySelector(
    ".product-main__swiper-thumbs"
  ) as HTMLElement | null;
  destroySwiper(thumbsEl);

  // Count slides before Swiper mutates the DOM (loop clones, etc.)
  const slideCount = mainEl.querySelectorAll(
    ".swiper-wrapper > .swiper-slide"
  ).length;
  if (slideCount === 0) {
    return;
  }

  const useLoop = slideCount > 1;
  let thumbsSwiper: any = null;

  if (thumbsEl && thumbsEl.querySelectorAll(".swiper-slide").length > 0) {
    thumbsSwiper = new SwiperCtor(thumbsEl, {
      spaceBetween: 8,
      slidesPerView: 3.5,
      breakpoints: {
        620: { slidesPerView: 5, spaceBetween: 13 },
      },
      watchSlidesProgress: true,
      // freeMode swallows click/tap on thumbs — keep it off so clicks register
      slideToClickedSlide: true,
    });
  }

  const nextEl = root.querySelector(".product-main__nav--next");
  const prevEl = root.querySelector(".product-main__nav--prev");

  const syncThumbActive = (realIndex: number) => {
    if (!thumbsEl) {
      return;
    }

    const thumbSlides = thumbsEl.querySelectorAll(
      ".swiper-wrapper > .swiper-slide"
    );
    thumbSlides.forEach((slide, index) => {
      slide.classList.toggle("swiper-slide-thumb-active", index === realIndex);
    });

    if (thumbsSwiper && typeof thumbsSwiper.slideTo === "function") {
      thumbsSwiper.slideTo(realIndex);
    }
  };

  const options: Record<string, unknown> = {
    // Avoid loop + thumbs: loop's cloned slides break index mapping on click.
    // Navigation arrows still work without loop.
    loop: false,
    observer: true,
    observeParents: true,
    on: {
      init(sw: any) {
        syncThumbActive(sw.realIndex ?? sw.activeIndex ?? 0);
      },
      slideChange(sw: any) {
        syncThumbActive(sw.realIndex ?? sw.activeIndex ?? 0);
      },
    },
  };

  if (nextEl && prevEl) {
    options.navigation = { nextEl, prevEl };
  }

  const mainSwiper = new SwiperCtor(mainEl, options);

  const goToIndex = (index: number) => {
    if (typeof index !== "number" || index < 0 || index >= slideCount) {
      return;
    }
    mainSwiper.slideTo(index);
    syncThumbActive(index);
  };

  if (thumbsEl) {
    // Capture phase so Swiper's own touch handlers can't suppress the click.
    thumbsEl.addEventListener(
      "click",
      (event) => {
        const target = event.target as Element | null;
        const slide = target?.closest?.(
          ".product-main__thumb-slide, .swiper-slide"
        ) as HTMLElement | null;

        if (!slide || !thumbsEl.contains(slide)) {
          return;
        }

        const thumbSlides = Array.from(
          thumbsEl.querySelectorAll(".swiper-wrapper > .swiper-slide")
        );
        const index = thumbSlides.indexOf(slide);
        if (index === -1) {
          return;
        }

        event.preventDefault();
        goToIndex(index);
      },
      true
    );

    // Also bind Swiper's click (sets clickedIndex) as a secondary path
    if (thumbsSwiper && typeof thumbsSwiper.on === "function") {
      thumbsSwiper.on("click", () => {
        if (typeof thumbsSwiper.clickedIndex === "number") {
          goToIndex(thumbsSwiper.clickedIndex);
        }
      });
    }
  }

  // Recalculate after Layzr swaps data-normal → src and images paint
  window.requestAnimationFrame(() => {
    mainSwiper.update?.();
    thumbsSwiper?.update?.();
    syncThumbActive(mainSwiper.realIndex ?? mainSwiper.activeIndex ?? 0);
  });
}

function getProductPath(url: string): string | null {
  try {
    const parsed = new URL(url, window.location.origin);
    if (!/\/products\/[^/?#]+/.test(parsed.pathname)) {
      return null;
    }
    return parsed.pathname + parsed.search;
  } catch (_e) {
    return null;
  }
}

function urlsMatch(a: string, b: string): boolean {
  try {
    const urlA = new URL(a, window.location.origin);
    const urlB = new URL(b, window.location.origin);
    return urlA.pathname === urlB.pathname;
  } catch (_e) {
    return a === b;
  }
}

function updateDocumentTitle(product: { title?: string }) {
  if (!product?.title) {
    return;
  }

  const current = document.title;
  const separator = " – ";
  const sepIndex = current.lastIndexOf(separator);
  if (sepIndex !== -1) {
    document.title = `${product.title}${separator}${current.slice(sepIndex + separator.length)}`;
  } else {
    document.title = product.title;
  }
}

function syncProductDataAttrs(
  section: HTMLElement,
  product: {
    available?: boolean;
    title?: string;
    selected_or_first_available_variant?: { id?: number; price?: number };
    variants?: Array<{ id?: number; price?: number; available?: boolean }>;
    sku?: string;
  }
) {
  const variant =
    product.selected_or_first_available_variant ||
    product.variants?.find((v) => v.available) ||
    product.variants?.[0];

  if (variant?.id != null) {
    section.dataset.productVariantId = String(variant.id);
  }
  if (typeof product.available === "boolean") {
    section.dataset.productInStock = String(product.available);
  }

  const newProduct = document.getElementById("new-product");
  if (!newProduct) {
    return;
  }

  if (typeof product.available === "boolean") {
    newProduct.dataset.available = String(product.available);
  }
  if (variant?.id != null) {
    newProduct.dataset.variantId = String(variant.id);
  }
  if (product.title) {
    newProduct.dataset.title = product.title;
  }
  if (variant?.price != null) {
    newProduct.dataset.unitPrice = String(variant.price);
  }
  if (product.sku != null) {
    newProduct.dataset.sku = String(product.sku);
  }
}

function remountProductMain(section: HTMLElement) {
  const container = section.querySelector(".product-main__container");
  if (!container) {
    return;
  }

  // Remount Vue modules inside the replaced container only
  // (avoid re-entering product-main / circular init).
  container.querySelectorAll("[data-module]").forEach((module) => {
    const el = module as HTMLElement;
    const name = el.dataset.module;

    if (name === "product-form") {
      productForm(el);
    } else if (name === "product-accordion") {
      productAccordion(el);
    } else if (name === "modal") {
      modalModule(el);
    } else if (name === "image") {
      imageModule(el);
    }
  });

  // Ensure Layzr picks up newly injected gallery images even if no
  // data-module pass ran (image module updates the shared instance).
  imageModule(container);

  moveKlaviyoStars(container);
  initProductGallery(section);
  initTippy(container);
  bindSellingPlanToggles(container);
}

async function swapProduct(
  url: string,
  options: { pushState?: boolean } = {}
) {
  const { pushState = true } = options;
  const path = getProductPath(url);

  if (!path) {
    window.location.href = url;
    return;
  }

  if (urlsMatch(path, window.location.href) && pushState) {
    return;
  }

  const section = document.querySelector(".product-main") as HTMLElement | null;
  if (!section) {
    window.location.href = url;
    return;
  }

  if (swapAbort) {
    swapAbort.abort();
  }
  swapAbort = new AbortController();
  swapInFlight = true;
  section.classList.add("is-loading");

  try {
    const separator = path.includes("?") ? "&" : "?";
    const response = await fetch(
      `${path}${separator}section_id=${SECTION_ID}`,
      {
        signal: swapAbort.signal,
        headers: { Accept: "text/html" },
      }
    );

    if (!response.ok) {
      throw new Error(`Section render failed: ${response.status}`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const nextContainer = doc.querySelector(".product-main__container");
    const productJsonEl = doc.querySelector("[data-product-json]");
    const liveContainer = section.querySelector(".product-main__container");

    if (!nextContainer || !liveContainer || !productJsonEl) {
      throw new Error("Missing product-main markup in section response");
    }

    let product: any;
    try {
      product = JSON.parse(productJsonEl.textContent || "{}");
    } catch (_e) {
      throw new Error("Invalid product JSON in section response");
    }

    (window as any).BARREL = (window as any).BARREL || {};
    (window as any).BARREL.product = product;

    liveContainer.replaceWith(document.importNode(nextContainer, true));
    syncProductDataAttrs(section, product);
    updateDocumentTitle(product);

    if (pushState) {
      history.pushState({ linkedProduct: true }, "", path);
    }

    remountProductMain(section);
  } catch (error: any) {
    if (error?.name === "AbortError") {
      return;
    }
    console.error(error);
    window.location.href = url;
  } finally {
    swapInFlight = false;
    section.classList.remove("is-loading");
  }
}

function bindLinkedProductClicks(section: HTMLElement) {
  if ((section as any)[BOUND_FLAG]) {
    return;
  }
  (section as any)[BOUND_FLAG] = true;

  section.addEventListener("click", (event) => {
    const target = event.target as Element | null;
    const link = target?.closest?.(
      ".linked-product__tile-link"
    ) as HTMLAnchorElement | null;

    if (!link || !section.contains(link)) {
      return;
    }

    const href = link.getAttribute("href");
    if (!href || !getProductPath(href)) {
      return;
    }

    // Allow modified clicks (new tab, etc.)
    const mouseEvent = event as MouseEvent;
    if (
      mouseEvent.metaKey ||
      mouseEvent.ctrlKey ||
      mouseEvent.shiftKey ||
      mouseEvent.altKey ||
      mouseEvent.button !== 0
    ) {
      return;
    }

    if (link.getAttribute("aria-current") === "page") {
      event.preventDefault();
      return;
    }

    if (swapInFlight) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    void swapProduct(href, { pushState: true });
  });
}

function bindPopState() {
  if (popstateBound) {
    return;
  }
  popstateBound = true;

  window.addEventListener("popstate", () => {
    if (!document.querySelector(".product-main")) {
      return;
    }
    void swapProduct(window.location.href, { pushState: false });
  });
}

export default (el: HTMLElement) => {
  moveKlaviyoStars(el);
  initProductGallery(el);
  bindLinkedProductClicks(el);
  bindPopState();
};
